<template>
  <div class="p-4 glass-frosted h-full flex flex-col rounded-8xl">
    <p class="text-black-white text-l-medium mb-3">{{ $t('parameters.wheelSpeed.title') }}</p>
    <!-- TODO: 滑条 -->

    <div class="flex-1 flex flex-col gap-2">
      <div
        class="px-4 flex-1 flex items-center justify-between gap-3 rounded-4xl cursor-pointer"
        :class="ctx.state.wheelScrollMode.value === 'line' ? 'bg-white' : 'bg-key'"
        @click="() => handleChangeTab('line')"
      >
        <Icon
          icon="Writing-Wireframe-ScrollLine"
          class="size-9 transition-colors"
          :class="
            ctx.state.wheelScrollMode.value === 'line'
              ? 'text-brand-blue2'
              : 'text-black-white group-hover:text-text-glass-hover'
          "
        />
        <div class="flex-1 flex flex-col gap-2">
          <p
            class="text-m-medium transition-colors"
            :class="
              ctx.state.wheelScrollMode.value === 'line'
                ? 'text-brand-blue2'
                : 'text-black-white group-hover:text-text-glass-hover'
            "
          >
            {{ $t('parameters.wheelSpeed.wheelLinesTitle') }}
          </p>
          <p
            class="text-s-medium transition-colors"
            :class="
              ctx.state.wheelScrollMode.value === 'line'
                ? 'text-blue-dark-hover'
                : 'text-text-glass-default group-hover:text-text-glass-hover'
            "
          >
            {{ $t('parameters.wheelSpeed.wheelLinesDesc') }}
          </p>
        </div>
        <div class="p-1.5 glass-frosted rounded-4xl">
          <div class="flex items-center gap-1">
            <Button
              color="blue1"
              shape="circle"
              size="icon-xs"
              :disabled="currentValue <= WHEEL_MIN"
              @click.stop="decreaseLoop"
            >
              <Icon icon="Status-Wireframe-Collapse" />
            </Button>
            <input
              v-model="lineValue"
              type="text"
              inputmode="numeric"
              class="no-spinner text-center px-2 w-10 h-6 text-text-body-default rounded-full bg-white outline-none"
              @input="handleInput"
              @blur="handleBlur"
              @keyup.enter="handleEnter"
            />
            <Button
              color="blue1"
              shape="circle"
              size="icon-xs"
              :disabled="currentValue >= WHEEL_MAX"
              @click.stop="increaseLoop"
            >
              <Icon icon="Function-Wireframe-Add11" />
            </Button>
          </div>
        </div>
      </div>
      <div
        class="group px-4 flex-1 flex items-center justify-between gap-3 rounded-3xl cursor-pointer"
        :class="ctx.state.wheelScrollMode.value === 'page' ? 'bg-white' : 'bg-key'"
        @click="() => handleChangeTab('page')"
      >
        <Icon
          icon="Writing-Wireframe-ScrollPage"
          class="size-9 transition-colors"
          :class="
            ctx.state.wheelScrollMode.value === 'page'
              ? 'text-brand-blue2'
              : 'text-black-white group-hover:text-text-glass-hover'
          "
        />
        <div class="flex-1 flex flex-col gap-2">
          <p
            class="text-m-medium transition-colors"
            :class="
              ctx.state.wheelScrollMode.value === 'page'
                ? 'text-brand-blue2'
                : 'text-black-white group-hover:text-text-glass-hover'
            "
          >
            {{ $t('parameters.wheelSpeed.wheelPageTitle') }}
          </p>
          <p
            class="text-s-medium transition-colors"
            :class="
              ctx.state.wheelScrollMode.value === 'page'
                ? 'text-blue-dark-hover'
                : 'text-text-glass-default group-hover:text-text-glass-hover'
            "
          >
            {{ $t('parameters.wheelSpeed.wheelPageDesc') }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue'
  import { Icon, Button } from '@rapoo/element'
  import type { ParametersContext } from '../../composables/useParameters'
  import type { ModuleSize } from '../utils/types'

  const props = defineProps<{
    parameters: ParametersContext
    size?: ModuleSize
  }>()

  const WHEEL_MIN = 1
  const WHEEL_MAX = 100

  const ctx = props.parameters

  const lineValue = ref(String(ctx.state.wheelSpeed.value))

  // 当设备初始化完成后同步一次行数显示值
  watch(ctx.state.wheelSpeed, (val) => {
    lineValue.value = String(val)
  })

  // 输入框为空时回退到已生效的值，保证步进和禁用判断始终有数可用
  const currentValue = computed(() => {
    const parsed = Number.parseInt(lineValue.value, 10)
    return Number.isNaN(parsed) ? ctx.state.wheelSpeed.value : parsed
  })

  const handleChangeTab = (tab: 'line' | 'page') => {
    if (tab === ctx.state.wheelScrollMode.value) return
    ctx.actions.setWheelScrollMode(tab)
  }

  const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

  /** input 事件过滤：剔除所有非数字字符后钳到 WHEEL_MIN–WHEEL_MAX */
  const handleInput = (e: Event) => {
    const input = e.target as HTMLInputElement
    let next = input.value.replace(/\D/g, '')

    if (next !== '') {
      next = String(clamp(Number.parseInt(next, 10), WHEEL_MIN, WHEEL_MAX))
    }

    if (next !== input.value) {
      input.value = next
    }
    lineValue.value = next
  }

  const stepBy = (delta: number) => {
    const value = clamp(currentValue.value + delta, WHEEL_MIN, WHEEL_MAX)
    lineValue.value = String(value)
    ctx.actions.setWheelSpeed(value)
  }

  const increaseLoop = () => stepBy(1)
  const decreaseLoop = () => stepBy(-1)

  const commitValue = () => {
    const value = clamp(currentValue.value, WHEEL_MIN, WHEEL_MAX)
    lineValue.value = String(value)
    ctx.actions.setWheelSpeed(value)
  }

  const handleBlur = () => commitValue()
  const handleEnter = () => commitValue()
</script>

<style scoped>
  .no-spinner::-webkit-outer-spin-button,
  .no-spinner::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .no-spinner {
    appearance: textfield;
    -moz-appearance: textfield;
  }
</style>
